import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ToolbarComponent } from '../../../../core/layout/components/toolbar/toolbar.component';
import { CredibilityComponent } from '../../../../core/layout/components/credibility/credibility.component';
import { AdoptionStageComponent } from '../../../../core/layout/components/adoption-stage/adoption-stage.component';
import { UsefulWeeksComponent } from '../../../../core/layout/components/useful-weeks/useful-weeks.component';
import { CriticalMassComponent } from '../../../../core/layout/components/critical-mass/critical-mass.component';
import { CommonModule } from '@angular/common';
import { GameService } from '../../../../shared/services/game.service';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import {
  ConfirmationDialogComponent,
  ConfirmationDialogData,
} from '../../../../shared/components/confirmation-dialog/confirmation-dialog.component';
import { lastValueFrom } from 'rxjs';

@Component({
  selector: 'app-game-over',
  standalone: true,
  imports: [
    ToolbarComponent,
    CredibilityComponent,
    AdoptionStageComponent,
    UsefulWeeksComponent,
    CriticalMassComponent,
    CommonModule,
    MatSnackBarModule,
  ],
  templateUrl: './game-over.component.html',
  styleUrls: ['./game-over.component.scss'],
})
export class GameOverComponent implements OnInit {
  adoptionStageValue = 0;
  usefulWeeksValue = 0;
  criticalMass = 0;
  scenarioId = '';
  credibility = 0;
  result = '';
  prid = '';

  screenMessage: {
    result?: string;
    title: string;
    subtitle: string;
  } = {
    result: '',
    title: '',
    subtitle: '',
  };

  constructor(
    readonly router: Router,
    readonly route: ActivatedRoute,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    readonly _gameService: GameService
  ) {
    // GET NAVIGATION STATE DATA
    const navigation = this.router.getCurrentNavigation();
    if (navigation?.extras.state) {
      this.adoptionStageValue = navigation.extras.state['adoptionStage'] || 0;
      this.usefulWeeksValue = navigation.extras.state['usefulWeeks'] || 0;
      this.criticalMass = navigation.extras.state['criticalMass'] || 0;
      this.credibility = navigation.extras.state['credibility'] || 0;
      this.scenarioId = navigation.extras.state['scenarioId'] || '';
      this.prid = navigation.extras.state['prid'] || '';
    }
  }

  ngOnInit(): void {
    this.initializeResult();
    this.setScreenMessage();
  }

  initializeResult(): void {
    this.result = this.route.snapshot.paramMap.get('result') || '';
  }

  setScreenMessage(): void {
    const messageConfig = this.getMessageConfig();
    if (messageConfig) {
      this.screenMessage = messageConfig;
    }
  }

  getMessageConfig() {
    const scenarios = {
      '1': {
        win: {
          result: 'Congratulations!',
          title:
            'The WorkSync transformation is complete, and adoption was achieved ahead of schedule. By driving awareness, engagement, and sustained execution, the organization now benefits from streamlined processes, faster approvals, and improved data visibility.',
          subtitle:
            'These results reflect the power of effective change management in delivering lasting business impact.',
        },
        lose: {
          title:
            'WorkSync adoption was not achieved within the 80-week timeframe. As a result, approvals remain slow, manual processes persist, and leadership has limited visibility into workforce activity.',
          subtitle:
            'The experience highlights the importance of early communication, structured execution, and consistent reinforcement to drive successful transformation.',
        },
      },
      '2': {
        win: {
          result: 'Congratulations!',
          title:
            'The cloud migration is complete, and adoption was achieved ahead of schedule. Your leadership and timely actions minimized disruption and ensured employees embraced the new environment.',
          subtitle:
            'The organization now benefits from faster operations, reduced reliance on legacy systems, and greater scalability to meet future needs. This transformation positions IT as a driver of innovation and long-term business impact.',
        },
        lose: {
          title:
            'The cloud migration was not fully achieved within the expected timeframe. While some progress was made, legacy systems remain in use, slowing operations and limiting access to the full benefits of the cloud.',
          subtitle:
            'This outcome highlights the importance of early engagement, clear communication, and sustained leadership support to drive lasting transformation.',
        },
      },
    };

    const scenario = scenarios[this.scenarioId as keyof typeof scenarios];
    if (!scenario) return null;

    return scenario[this.result as keyof typeof scenario] || null;
  }

  async startOver() {
    // Show confirmation dialog
    const dialogData: ConfirmationDialogData = {
      title: 'Restart Simulation',
      message:
        'Are you sure you want to restart the simulation? This action cannot be undone and you will lose all current progress.',
      confirmText: 'Yes, restart',
      cancelText: 'Cancel',
      icon: 'restart_alt',
      isDangerous: true,
    };

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '400px',
      data: dialogData,
      disableClose: true,
    });

    const confirmed = await lastValueFrom(dialogRef.afterClosed());

    if (!confirmed) {
      return; // User cancelled
    }

    try {
      const response = await this._gameService.startOver(
        this.prid,
        this.scenarioId
      );

      if (response && response.success) {
        // SUCCESS TOAST
        this.snackBar.open(
          `The scenario ${this.scenarioId} has been reset for user ${this.prid} successfully`,
          'Close',
          {
            duration: 5000,
            panelClass: ['success-toast'],
          }
        );
        this.returnToHome();
      } else {
        // ERROR TOAST
        this.snackBar.open(
          'Failed to reset the simulator. Please try again.',
          'Close',
          {
            duration: 5000,
            panelClass: ['error-toast'],
          }
        );
      }
    } catch (error) {
      console.error('Error in startOver:', error);
      // ERROR TOAST
      this.snackBar.open(
        'Failed to reset the simulator. Please try again.',
        'Close',
        {
          duration: 5000,
          panelClass: ['error-toast'],
        }
      );
    }
  }

  returnToHome() {
    this.router.navigate(['/dashboard']);
  }
}
